/**
 * Error Handling Utilities
 * Provides functions for converting form errors to ErrorSummary format
 * and generating recovery guidance for common validation errors
 */

interface ErrorItem {
  fieldId: string
  fieldLabel: string
  message: string
  severity: 'error' | 'warning' | 'info'
  recoveryGuidance?: string
}

interface FieldConfig {
  label: string
  type?: 'number' | 'text' | 'select' | 'city'
}

// Field configuration mapping
const FIELD_CONFIGS: Record<string, FieldConfig> = {
  area: { label: 'Area', type: 'number' },
  thickness: { label: 'Thickness', type: 'number' },
  facilityLife: { label: 'Facility Life', type: 'number' },
  dosageBags: { label: 'Dosage Bags', type: 'select' },
  deliveryCity: { label: 'Delivery City', type: 'city' },
  downtimeHours: { label: 'Downtime Hours', type: 'number' },
  customDowntimeCost: { label: 'Custom Downtime Cost', type: 'number' },
  customMarkup: { label: 'Custom Markup', type: 'number' },
  industry: { label: 'Industry', type: 'select' },
  markup: { label: 'Markup', type: 'select' }
}

// Recovery guidance templates
const RECOVERY_GUIDANCE: Record<string, string> = {
  area: 'Enter a valid area between 1 and 1,000,000 square units. Use whole numbers only.',
  thickness: 'Enter a thickness value greater than 0. Typical values range from 2 to 12 inches.',
  facilityLife: 'Enter a facility life between 1 and 50 years. This represents how long the facility will be in use.',
  dosageBags: 'Select a valid number of dosage bags from the dropdown. The available options depend on your unit system.',
  deliveryCity: 'Enter a valid city name or select from the suggestions. The city is used to calculate freight costs.',
  downtimeHours: 'Enter downtime hours between 0 and 8760 (hours in a year). This represents potential downtime per incident.',
  customDowntimeCost: 'Enter a positive dollar amount for your custom downtime cost per hour.',
  customMarkup: 'Enter a non-negative percentage for your custom markup.',
  industry: 'Select an industry from the dropdown to get appropriate default values.',
  markup: 'Select a markup option from the dropdown.'
}

/**
 * Converts form validation errors to ErrorSummary format
 */
export function convertFormErrorsToSummary(
  formErrors: Record<string, string>,
  severity: 'error' | 'warning' | 'info' = 'error'
): ErrorItem[] {
  return Object.entries(formErrors).map(([fieldId, message]) => {
    const config = FIELD_CONFIGS[fieldId] || { label: fieldId }
    
    return {
      fieldId,
      fieldLabel: config.label,
      message,
      severity,
      recoveryGuidance: RECOVERY_GUIDANCE[fieldId]
    }
  })
}

/**
 * Creates calculation error items for display in results area
 */
export function createCalculationError(
  error: string,
  errorCode?: string
): ErrorItem[] {
  const baseError: ErrorItem = {
    fieldId: 'calculation',
    fieldLabel: 'Calculation Error',
    message: error,
    severity: 'error' as const,
    recoveryGuidance: getCalculationRecoveryGuidance(errorCode)
  }

  return [baseError]
}

/**
 * Gets recovery guidance for calculation errors
 */
function getCalculationRecoveryGuidance(errorCode?: string): string {
  switch (errorCode) {
    case 'NETWORK_ERROR':
      return 'Check your internet connection and try again. If the problem persists, please contact support.'
    case 'INVALID_CITY':
      return 'Please select a valid city from the suggestions or try a different city name.'
    case 'FREIGHT_CALCULATION_ERROR':
      return 'There was an issue calculating freight costs. Please verify your delivery city and try again.'
    case 'VALIDATION_ERROR':
      return 'Please check all your input values and ensure they are within the valid ranges.'
    case 'SERVER_ERROR':
      return 'Our servers are experiencing issues. Please try again in a few moments.'
    case 'TIMEOUT_ERROR':
      return 'The calculation is taking longer than expected. Please try again with a simpler configuration.'
    default:
      return 'Please review your inputs and try again. If the problem continues, contact support for assistance.'
  }
}

/**
 * Combines multiple error sources into a single array
 */
export function combineErrors(...errorSources: ErrorItem[][]): ErrorItem[] {
  return errorSources.flat().filter(Boolean)
}

/**
 * Groups errors by severity for display
 */
export function groupErrorsBySeverity(errors: ErrorItem[]): Record<string, ErrorItem[]> {
  return errors.reduce((groups, error) => {
    const severity = error.severity
    if (!groups[severity]) {
      groups[severity] = []
    }
    groups[severity].push(error)
    return groups
  }, {} as Record<string, ErrorItem[]>)
}

/**
 * Checks if there are any errors of a specific severity
 */
export function hasErrorsOfSeverity(errors: ErrorItem[], severity: 'error' | 'warning' | 'info'): boolean {
  return errors.some(error => error.severity === severity)
}

/**
 * Gets the highest severity level from a list of errors
 */
export function getHighestSeverity(errors: ErrorItem[]): 'error' | 'warning' | 'info' | null {
  if (hasErrorsOfSeverity(errors, 'error')) return 'error'
  if (hasErrorsOfSeverity(errors, 'warning')) return 'warning'
  if (hasErrorsOfSeverity(errors, 'info')) return 'info'
  return null
}

/**
 * Creates a warning for potential issues that don't prevent calculation
 */
export function createWarning(
  fieldId: string,
  message: string,
  recoveryGuidance?: string
): ErrorItem {
  const config = FIELD_CONFIGS[fieldId] || { label: fieldId }
  
  return {
    fieldId,
    fieldLabel: config.label,
    message,
    severity: 'warning',
    recoveryGuidance: recoveryGuidance || RECOVERY_GUIDANCE[fieldId]
  }
}

/**
 * Creates an info message for helpful tips
 */
export function createInfo(
  fieldId: string,
  message: string,
  recoveryGuidance?: string
): ErrorItem {
  const config = FIELD_CONFIGS[fieldId] || { label: fieldId }
  
  return {
    fieldId,
    fieldLabel: config.label,
    message,
    severity: 'info',
    recoveryGuidance
  }
}

/**
 * Validates if errors should block calculation
 */
export function shouldBlockCalculation(errors: ErrorItem[]): boolean {
  return hasErrorsOfSeverity(errors, 'error')
}

/**
 * Formats error count for display
 */
export function formatErrorCount(errors: ErrorItem[]): string {
  const errorCount = errors.filter(e => e.severity === 'error').length
  const warningCount = errors.filter(e => e.severity === 'warning').length
  const infoCount = errors.filter(e => e.severity === 'info').length
  
  const parts: string[] = []
  if (errorCount > 0) parts.push(`${errorCount} error${errorCount !== 1 ? 's' : ''}`)
  if (warningCount > 0) parts.push(`${warningCount} warning${warningCount !== 1 ? 's' : ''}`)
  if (infoCount > 0) parts.push(`${infoCount} info`)
  
  return parts.join(', ')
}

export type { ErrorItem, FieldConfig }

/**
 * Announces a message to screen readers
 * Creates a temporary live region for accessibility announcements
 */
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite'): void => {
  const announcement = document.createElement('div')
  announcement.setAttribute('aria-live', priority)
  announcement.setAttribute('aria-atomic', 'true')
  announcement.className = 'sr-only'
  announcement.style.position = 'absolute'
  announcement.style.left = '-9999px'
  announcement.style.width = '1px'
  announcement.style.height = '1px'
  announcement.style.overflow = 'hidden'
  announcement.textContent = message
  
  document.body.appendChild(announcement)
  
  // Clean up after announcement
  setTimeout(() => {
    if (document.body.contains(announcement)) {
      document.body.removeChild(announcement)
    }
  }, 1000)
}

/**
 * Announces error corrections to screen readers
 */
export const announceErrorCorrection = (fieldLabel: string, isFixed: boolean): void => {
  const message = isFixed 
    ? `${fieldLabel} error has been corrected`
    : `${fieldLabel} still has an error that needs to be fixed`
  
  announceToScreenReader(message, 'polite')
}

/**
 * Announces validation state changes
 */
export const announceValidationState = (totalErrors: number, previousErrors: number): void => {
  if (totalErrors === 0 && previousErrors > 0) {
    announceToScreenReader('All validation errors have been corrected. You can now calculate ROI.', 'polite')
  } else if (totalErrors > previousErrors) {
    const newErrors = totalErrors - previousErrors
    announceToScreenReader(`${newErrors} new validation ${newErrors === 1 ? 'error' : 'errors'} found. Please review and correct.`, 'assertive')
  } else if (totalErrors < previousErrors) {
    const fixedErrors = previousErrors - totalErrors
    announceToScreenReader(`${fixedErrors} validation ${fixedErrors === 1 ? 'error' : 'errors'} corrected. ${totalErrors} ${totalErrors === 1 ? 'error remains' : 'errors remain'}.`, 'polite')
  }
}